import React, {
    memo,
    useCallback,
    useEffect,
    useRef,
    type DependencyList,
    type MutableRefObject,
    type ReactNode,
    type RefObject,
} from 'react'
import { ScrollResizeCompensationProvider } from 'lib/scrollResizeCompensationContext'
import { RowGeometryEventsProvider } from 'lib/rowGeometryEvents'
import { usePanelVirtualizer } from 'lib/usePanelVirtualizer'
import { updateStaticRowQaProbe } from 'lib/staticGeometryQaProbe'
import styles from 'components/panels/panel.module.scss'

export type VirtualQuestGeometryRow = {
    key: string | number | bigint
    estimatedHeight: number
    staticSignature: string | null
    resizeIntentSignature: string | null
}

type QuestGeometryState = {
    revision?: number
    expandedTweetAnswerIds?: string[]
    editingAnswerIds?: string[]
}

type Props<Row> = {
    rows: Row[]
    geometryRows: ReadonlyArray<VirtualQuestGeometryRow>
    getQuestId: (row: Row) => string | null
    geometryStateByQuestId: Record<string, QuestGeometryState>
    renderRow: (row: Row, index: number) => ReactNode
    resolveScrollElement: () => HTMLElement | null
    staticGeometry: boolean
    measureDeps: DependencyList
    scrollMarginDeps: DependencyList
    observeElementRefs: Array<RefObject<Element | null>>
    registerStaticRow: (node: HTMLElement | null, key: string | number | bigint) => void
    onTweetExpandedChange: (change: unknown) => void
    onAnswerEditingChange: (change: unknown) => void
    hasNextPage: boolean
    isFetchingNextPage: boolean
    fetchNextPage: () => unknown
    prepareForNextPage: () => void
    scrollDirectionRef: MutableRefObject<'down' | 'up' | 'none'>
    paginationTailHeight: number
    paginationTailReserveHeight: number
    shouldRenderPaginationTail: boolean
    overscan: number
    gap: number
    prefetchDistance: number
}

function VirtualQuestViewportInner<Row>({
    rows,
    geometryRows,
    getQuestId,
    geometryStateByQuestId,
    renderRow,
    resolveScrollElement,
    staticGeometry,
    measureDeps,
    scrollMarginDeps,
    observeElementRefs,
    registerStaticRow,
    onTweetExpandedChange,
    onAnswerEditingChange,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    prepareForNextPage,
    scrollDirectionRef,
    paginationTailHeight,
    paginationTailReserveHeight,
    shouldRenderPaginationTail,
    overscan,
    gap,
    prefetchDistance,
}: Props<Row>) {
    const fetchInFlightRef = useRef(false)
    const fetchFrameRef = useRef<number | null>(null)
    const lastScrollTopRef = useRef(0)
    const {
        listElementRef,
        measureElement,
        scrollElement,
        totalSize,
        virtualListStart,
        virtualItems,
    } = usePanelVirtualizer({
        count: rows.length,
        resolveScrollElement,
        getItemKey: index => geometryRows[index]?.key ?? `quest-row-${index}`,
        estimateSize: index => geometryRows[index]?.estimatedHeight ?? 1,
        overscan,
        gap,
        staticGeometry,
        staticRows: staticGeometry ? geometryRows : undefined,
        measureDeps,
        scrollMarginDeps,
        observeElementRefs,
        allowMeasuredShrinkAdjustment: true,
        retainMeasuredScrollMovement: false,
        retainBackwardMeasuredScrollMovement: true,
        retainBackwardVirtualStartOnTotalSizeChange: true,
        retainVirtualStartMovement: true,
    })

    const maybeFetchNextPage = useCallback(() => {
        if (
            !scrollElement ||
            !hasNextPage ||
            isFetchingNextPage ||
            scrollDirectionRef.current === 'up' ||
            fetchInFlightRef.current
        ) {
            return
        }

        const { scrollTop, scrollHeight, clientHeight } = scrollElement
        const distance = Math.max(prefetchDistance, clientHeight * 3)
        const loadedScrollHeight = Math.max(
            clientHeight,
            scrollHeight - paginationTailReserveHeight,
        )
        if (scrollTop + clientHeight < loadedScrollHeight - distance) return

        prepareForNextPage()
        fetchInFlightRef.current = true
        Promise.resolve(fetchNextPage()).finally(() => {
            fetchInFlightRef.current = false
        })
    }, [
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        paginationTailReserveHeight,
        prefetchDistance,
        prepareForNextPage,
        scrollDirectionRef,
        scrollElement,
    ])

    const scheduleMaybeFetchNextPage = useCallback(() => {
        if (fetchFrameRef.current != null) return
        fetchFrameRef.current = window.requestAnimationFrame(() => {
            fetchFrameRef.current = null
            maybeFetchNextPage()
        })
    }, [maybeFetchNextPage])

    useEffect(() => {
        if (!scrollElement) return

        lastScrollTopRef.current = scrollElement.scrollTop
        const onScroll = () => {
            const nextScrollTop = scrollElement.scrollTop
            if (nextScrollTop > lastScrollTopRef.current) {
                scrollDirectionRef.current = 'down'
            } else if (nextScrollTop < lastScrollTopRef.current) {
                scrollDirectionRef.current = 'up'
            }
            lastScrollTopRef.current = nextScrollTop
            scheduleMaybeFetchNextPage()
        }

        scrollElement.addEventListener('scroll', onScroll, { passive: true })
        scheduleMaybeFetchNextPage()

        return () => {
            scrollElement.removeEventListener('scroll', onScroll)
            if (fetchFrameRef.current != null) {
                window.cancelAnimationFrame(fetchFrameRef.current)
                fetchFrameRef.current = null
            }
        }
    }, [scheduleMaybeFetchNextPage, scrollDirectionRef, scrollElement])

    useEffect(() => {
        if (!scrollElement) return
        scheduleMaybeFetchNextPage()
    }, [rows.length, scheduleMaybeFetchNextPage, scrollElement, totalSize])

    return (
        <div
            ref={listElementRef}
            className={styles.virtualQuestList}
            data-virtual-pagination-tail-height={
                shouldRenderPaginationTail ? Math.round(paginationTailHeight) : undefined
            }
            style={{ height: totalSize + paginationTailReserveHeight }}
        >
            {virtualItems.map(virtualItem => {
                const row = rows[virtualItem.index]
                if (!row) return null
                const questId = getQuestId(row)
                if (!questId) return null
                const geometryState = geometryStateByQuestId[questId]

                return (
                    <div
                        key={virtualItem.key}
                        ref={node => {
                            measureElement(node)
                            updateStaticRowQaProbe(node, virtualItem.key)
                            registerStaticRow(node, virtualItem.key)
                        }}
                        data-virtual-quest-row="true"
                        data-virtual-row-key={staticGeometry ? String(virtualItem.key) : undefined}
                        data-virtual-static-geometry={staticGeometry ? 'true' : undefined}
                        data-virtual-row-size={
                            staticGeometry ? Math.round(virtualItem.size) : undefined
                        }
                        data-virtual-quest-id={questId}
                        data-virtual-geometry-revision={geometryState?.revision || 0}
                        data-index={virtualItem.index}
                        className={styles.virtualQuestRow}
                        style={{
                            height: staticGeometry ? virtualItem.size : undefined,
                            ...(staticGeometry && geometryState?.editingAnswerIds?.length
                                ? { overflow: 'visible', zIndex: 1 }
                                : null),
                            transform: `translateY(${virtualItem.start - virtualListStart}px)`,
                        }}
                    >
                        <ScrollResizeCompensationProvider compensate={false}>
                            <RowGeometryEventsProvider
                                questId={questId}
                                expandedTweetAnswerIds={geometryState?.expandedTweetAnswerIds}
                                onTweetExpandedChange={onTweetExpandedChange}
                                onAnswerEditingChange={onAnswerEditingChange}
                            >
                                {renderRow(row, virtualItem.index)}
                            </RowGeometryEventsProvider>
                        </ScrollResizeCompensationProvider>
                    </div>
                )
            })}
        </div>
    )
}

const VirtualQuestViewport = memo(VirtualQuestViewportInner) as typeof VirtualQuestViewportInner

export default VirtualQuestViewport
